/**
 * 视频管理 Composable
 * 管理视频列表获取、预加载等功能
 */

import axios from 'axios';
import config from '@/config';
import logger from '@/utils/logger';
import { parseApiResponse, snakeToCamel } from '@/utils/apiResponse';

export default function useVideoManager() {
  return {
    data() {
      return {
        videoList: [], // 存储视频列表
        videoTimer: null, // 视频轮询定时器
        isFetchingVideoList: false, // 是否正在获取视频列表，防止并发请求
        lastVideoListHash: '', // 上次视频列表的哈希值，用于比较是否变化
        lastVideoRequestTime: 0, // 上次视频请求时间，用于防抖
        videoRequestDebounceTime: 2000, // 视频请求防抖时间（2秒）
        preloadVideoElement: null, // 预加载视频元素引用
        preloadVideoUrl: '', // 预加载视频URL（缓存，避免频繁计算）
        lastPreloadVideoUrl: '', // 上次预加载的URL，用于比较
        videoPreloadStatus: 'idle', // 预加载状态：idle, loading, loaded, error
        videoPreloadTimer: null, // 预加载延迟定时器
        videoPreloadRetryTimer: null, // 预加载失败重试定时器，组件销毁时需清理
        cleanupPreloadVideoTimer: null, // 清理预加载视频时 300ms 延迟清空 src 的定时器
        videoPreloadRetryCount: 0, // 视频预加载重试次数
        maxPreloadRetryCount: 3, // 最大重试次数
        isVideoPlaying: false, // 视频是否正在播放
        videoPlayPromise: null, // 视频播放Promise，用于跟踪播放状态
        videoLoadCallCount: 0, // 视频load()调用计数，用于调试
        isPreloadVideoRequesting: false, // 是否正在请求预加载视频，防止并发
        currentPreloadVideoSrc: '', // 当前预加载视频的实际src，用于避免重复设置
        videoRequestCount: 0, // 视频请求计数器
        maxVideoRequestsPerSecond: 1 // 每秒最大请求次数，防止无限循环
      };
    },

    methods: {
      /**
       * 生成视频列表哈希值
       */
      generateVideoListHash(videoList) {
        if (!videoList || !Array.isArray(videoList) || videoList.length === 0) {
          return 'empty';
        }
        // 使用视频ID和数量生成简单哈希
        const ids = videoList
          .filter(v => v && v.id)
          .map(v => v.id)
          .sort()
          .join(',');
        return `${videoList.length}_${ids}`;
      },

      /**
       * 获取视频列表
       */
      async fetchVideoList() {
        // 防止并发请求
        if (this.isFetchingVideoList) {
          logger.debug('[视频列表] 请求进行中，跳过');
          return;
        }
        
        // 防抖：如果距离上次请求时间太短，跳过
        const now = Date.now();
        if (this.lastVideoRequestTime > 0 && (now - this.lastVideoRequestTime) < this.videoRequestDebounceTime) {
          logger.debug('[视频列表] 防抖：距离上次请求时间太短，跳过', {
            timeSinceLastRequest: now - this.lastVideoRequestTime,
            debounceTime: this.videoRequestDebounceTime
          });
          return;
        }
        
        if (this.isSliding) {
          return; // 滑动期间暂停视频列表更新，避免触发不必要渲染
        }
        
        const storeId = this.effectiveStoreId;
        if (!storeId) {
          return;
        }
        
        this.isFetchingVideoList = true;
        this.lastVideoRequestTime = now;
        
        const welcomeConfigId = this.welcomeConfig && this.welcomeConfig.id
          ? this.welcomeConfig.id
          : null;
        
        logger.debug('[视频列表] 开始请求', {
          storeId: storeId,
          welcomeConfigId,
          timestamp: new Date().toISOString()
        });
        
        try {
          // 使用门店视频列表接口：GET /api/store-welcome-video/store/{storeId}
          // 约定：enabled=1 时只返回“启用”的视频，用于实现“上传不等于启用，未启用不在大屏播放”
          const apiUrl = config.getApiUrl(
            config.apiEndpoints.welcomeVideo.store,
            storeId
          );
          
          const response = await axios.get(apiUrl, {
            params: { enabled: 1 }
          });
          
          // 使用统一的 ApiResponse 解析逻辑
          const parsed = parseApiResponse(response);
          
          if (!parsed.success || !parsed.data) {
            // 没有视频数据，检查并清空列表
            const newHash = 'empty';
            if (newHash !== this.lastVideoListHash) {
              this.videoList = [];
              this.lastVideoListHash = newHash;
            }
            return;
          }
          
          // 解析真正的列表数据：兼容 data 为数组或 { success, data: [...], ... } 结构
          let list = [];
          if (Array.isArray(parsed.data)) {
            // 后端直接返回数组：data: [...]
            list = parsed.data;
          } else if (parsed.data && Array.isArray(parsed.data.data)) {
            // 后端返回嵌套结构：data: { success, data: [...], count, ... }
            list = parsed.data.data;
          }
          
          // 将下划线字段名转换为驼峰命名，并为每个视频拼接播放 URL
          // 注意：后端若仍返回未启用的视频，这里会再做一次兜底过滤，确保大屏只播放启用的视频。
          const newVideoListAll = list.map(rawVideo => {
            const video = snakeToCamel(rawVideo);
            const url = video.id
              ? config.getApiUrl(config.apiEndpoints.welcomeVideo.get, video.id)
              : '';
            return {
              ...video,
              // 规范化启用字段，兼容后端返回 boolean / number / string
              isEnabled: video.isEnabled === true || video.isEnabled === 1 || video.isEnabled === '1',
              url
            };
          });

          // 兜底：仅保留启用的视频，确保“未启用不在大屏播放”
          const newVideoList = newVideoListAll.filter(v => v && v.isEnabled);
          
          // 生成新列表的哈希值
          const newHash = this.generateVideoListHash(newVideoList);
          
          // 只在视频列表真正变化时才更新，避免不必要的重新渲染和预加载
          if (newHash !== this.lastVideoListHash) {
            logger.debug('[视频列表] 列表已更新', {
              oldHash: this.lastVideoListHash,
              newHash: newHash,
              videoCount: newVideoList.length,
              timestamp: new Date().toISOString()
            });
            
            this.videoList = newVideoList;
            this.lastVideoListHash = newHash;
            
            // 更新预加载视频URL（videoList watcher会自动处理，这里显式调用确保初始化）
            this.$nextTick(() => {
              this.updatePreloadVideoUrl();
            });
          } else {
            logger.debug('[视频列表] 列表未变化，跳过更新', {
              hash: newHash,
              timestamp: new Date().toISOString()
            });
          }
        } catch (error) {
          // 友好的错误处理
          if (error.response && error.response.status === 404) {
            // 404表示该门店没有视频
            const newHash = 'empty';
            if (newHash !== this.lastVideoListHash) {
              this.videoList = [];
              this.lastVideoListHash = newHash;
            }
          }
        } finally {
          this.isFetchingVideoList = false;
        }
      },

      /**
       * 获取预加载视频的URL（内部方法，用于计算URL）
       */
      getPreloadVideoUrl() {
        if (!this.videoList || this.videoList.length === 0) {
          return '';
        }
        const firstVideo = this.videoList[0];
        // 优先使用列表中预先拼接好的 url 字段，兼容旧数据时回退到根据 id 生成
        if (firstVideo && firstVideo.url) {
          return firstVideo.url;
        }
        if (firstVideo && firstVideo.id) {
          return config.getApiUrl(config.apiEndpoints.welcomeVideo.get, firstVideo.id);
        }
        return '';
      },

      /**
       * 更新预加载视频URL（只在URL真正变化时更新）
       */
      updatePreloadVideoUrl() {
        const newUrl = this.getPreloadVideoUrl();
        
        // 严格比较URL，确保只在真正变化时才更新
        if (newUrl === this.preloadVideoUrl && newUrl === this.lastPreloadVideoUrl) {
          // URL完全相同，不做任何操作
          return;
        }
        
        const oldUrl = this.preloadVideoUrl;
        
        // 如果新URL和上次设置的URL相同，说明是重复调用，跳过
        if (newUrl === this.lastPreloadVideoUrl && newUrl) {
          return;
        }
        
        // 立即更新URL，确保preloadVideoUrl不为空
        this.preloadVideoUrl = newUrl;
        this.lastPreloadVideoUrl = newUrl; // 记录上次设置的URL
        
        // 如果URL为空，立即清空状态
        if (!newUrl) {
          this.videoPreloadStatus = 'idle';
          this.videoPreloadRetryCount = 0;
          this.currentPreloadVideoSrc = '';
          // 清空视频元素的src，避免继续请求
          if (this.$refs.preloadVideo) {
            this.$refs.preloadVideo.src = '';
            this.currentPreloadVideoSrc = '';
          }
          return;
        }
        
        // 如果URL变化，重置状态以便重新加载
        if (oldUrl) {
          // URL改变了，重置状态
          this.videoPreloadStatus = 'idle';
          this.videoPreloadRetryCount = 0;
          this.currentPreloadVideoSrc = '';
          if (this.addSystemLog) {
            this.addSystemLog(`视频URL已更新: ${oldUrl} -> ${newUrl}`);
          }
        } else {
          // 从无到有，初始化状态
          this.videoPreloadStatus = 'idle';
          this.videoPreloadRetryCount = 0;
          this.currentPreloadVideoSrc = '';
          if (this.addSystemLog) {
            this.addSystemLog(`视频URL已设置: ${newUrl}`);
          }
        }
        
        // 延迟调用preloadVideo()，确保其他关键资源优先加载
        if (this.videoPreloadTimer) {
          clearTimeout(this.videoPreloadTimer);
        }
        this.videoPreloadTimer = setTimeout(() => {
          this.videoPreloadTimer = null;
          // 延迟500ms后再调用预加载
          if (this.preloadVideoUrl && !this.isVideoPage) {
            this.preloadVideo();
          }
        }, 500); // 延迟500ms
      },

      /**
       * 预加载视频开始加载
       */
      onPreloadVideoLoadStart() {
        // 记录请求开始，但不做任何操作，避免触发循环
      },

      /**
       * 预加载视频元数据加载完成
       */
      onPreloadVideoLoaded() {
        // 元数据加载完成，但视频内容可能还未完全加载
        if (this.videoPreloadStatus === 'idle') {
          this.videoPreloadStatus = 'loading';
        }
        logger.debug('[视频预加载] 元数据加载完成', {
          url: this.preloadVideoUrl,
          readyState: this.$refs.preloadVideo ? this.$refs.preloadVideo.readyState : 'unknown',
          timestamp: new Date().toISOString()
        });
      },

      /**
       * 预加载视频可以完整播放
       */
      onPreloadVideoCanPlayThrough() {
        this.videoPreloadStatus = 'loaded';
        this.videoPreloadRetryCount = 0; // 重置重试次数
        this.isPreloadVideoRequesting = false; // 重置请求锁
        
        if (this.$refs.preloadVideo) {
          this.preloadVideoElement = this.$refs.preloadVideo;
          // 记录当前src，避免重复设置
          this.currentPreloadVideoSrc = this.$refs.preloadVideo.src || this.preloadVideoUrl;
        }
        
        if (this.addSystemLog) {
          this.addSystemLog('视频预加载成功，已缓存到本地');
        }
      },

      /**
       * 预加载视频加载失败
       */
      onPreloadVideoError(error) {
        this.isPreloadVideoRequesting = false; // 重置请求锁
        
        const errorDetails = {
          message: error.message || '未知错误',
          type: error.type || 'unknown',
          target: error.target ? {
            src: error.target.src,
            networkState: error.target.networkState,
            readyState: error.target.readyState,
            error: error.target.error
          } : null,
          videoUrl: this.preloadVideoUrl
        };
        
        // 检查是否是AbortError（主动取消），不重试
        const isAbortError = errorDetails.type === 'abort' || 
                            errorDetails.message?.includes('abort') ||
                            errorDetails.message?.includes('Abort');
        
        if (isAbortError) {
          // 请求被取消，不重试
          this.videoPreloadStatus = 'idle';
          this.videoPreloadRetryCount = 0;
          return;
        }
        
        if (this.addSystemLog) {
          this.addSystemLog(`视频预加载失败: ${errorDetails.message || '未知错误'}`);
        }
        
        // 如果重试次数未超过限制，延迟重试
        if (this.videoPreloadRetryCount < this.maxPreloadRetryCount) {
          this.videoPreloadRetryCount++;
          const retryDelay = this.videoPreloadRetryCount * 10000; // 递增延迟：10秒、20秒、30秒
          if (this.addSystemLog) {
            this.addSystemLog(`视频预加载将在${retryDelay / 1000}秒后重试（第${this.videoPreloadRetryCount}次）`);
          }
          if (this.videoPreloadRetryTimer) {
            clearTimeout(this.videoPreloadRetryTimer);
            this.videoPreloadRetryTimer = null;
          }
          this.videoPreloadRetryTimer = setTimeout(() => {
            this.videoPreloadRetryTimer = null;
            if (this.preloadVideoUrl &&
                this.videoPreloadStatus === 'error' &&
                !this.isPreloadVideoRequesting) {
              this.videoPreloadStatus = 'idle';
              this.preloadVideo();
            }
          }, retryDelay);
        } else {
          // 超过最大重试次数，标记为错误状态
          this.videoPreloadStatus = 'error';
          if (this.addSystemLog) {
            this.addSystemLog('视频预加载失败，已达到最大重试次数');
          }
        }
      },

      /**
       * 开始预加载视频
       */
      preloadVideo() {
        // 首先检查URL是否已设置，如果没有URL则直接返回
        if (!this.preloadVideoUrl) {
          return;
        }
        
        // 如果正在请求，直接返回，防止并发请求
        if (this.isPreloadVideoRequesting) {
          return;
        }
        
        // 如果当前在视频页，不预加载
        if (this.isVideoPage) {
          return;
        }
        
        // 如果已经在加载或已加载，检查URL是否变化
        if (this.videoPreloadStatus === 'loading' || this.videoPreloadStatus === 'loaded') {
          // 检查视频URL是否变化，如果变化了才需要重新加载
          const preloadedUrl = this.preloadVideoElement && this.preloadVideoElement.src;
          
          if (this.preloadVideoUrl && this.preloadVideoUrl === preloadedUrl) {
            // URL没变，不需要重新加载
            return;
          }
          // URL变了，需要重新加载，重置状态和重试次数
          this.videoPreloadStatus = 'idle';
          this.videoPreloadRetryCount = 0;
          this.isPreloadVideoRequesting = false; // 重置请求锁
        }
        
        // 清除之前的延迟定时器
        if (this.videoPreloadTimer) {
          clearTimeout(this.videoPreloadTimer);
          this.videoPreloadTimer = null;
        }
        
        // 延迟500ms后再开始预加载，确保关键数据（预约数据、实时数据）优先加载
        this.videoPreloadTimer = setTimeout(() => {
          this.videoPreloadTimer = null;
          
          // 延迟后再次检查URL，确保URL仍然有效
          if (!this.preloadVideoUrl) {
            if (this.addSystemLog) {
              this.addSystemLog('视频预加载取消：URL为空');
            }
            return;
          }
          
          // 再次检查是否在视频页
          if (this.isVideoPage) {
            return;
          }
          
          // 再次检查是否正在请求
          if (this.isPreloadVideoRequesting) {
            return;
          }
          
          // 检查视频元素是否已加载
          if (this.$refs.preloadVideo) {
            const videoElement = this.$refs.preloadVideo;
            const currentSrc = videoElement.src || '';
            const targetSrc = this.preloadVideoUrl;
            
            // 如果src相同且视频已加载，不需要重新加载
            if (currentSrc === targetSrc && videoElement.readyState >= 3) {
              this.videoPreloadStatus = 'loaded';
              this.preloadVideoElement = videoElement;
              this.currentPreloadVideoSrc = targetSrc;
              return;
            }
          }
          
          // 检查请求频率，防止无限循环
          const now = Date.now();
          if (this.lastVideoRequestTime > 0) {
            const timeSinceLastRequest = now - this.lastVideoRequestTime;
            if (timeSinceLastRequest < 1000) {
              // 如果距离上次请求不到1秒，增加计数器
              this.videoRequestCount++;
              if (this.videoRequestCount > this.maxVideoRequestsPerSecond) {
                // 请求过于频繁，停止请求
                if (this.addSystemLog) {
                  this.addSystemLog('视频请求过于频繁，已停止请求');
                }
                this.videoPreloadStatus = 'error';
                this.isPreloadVideoRequesting = false;
                return;
              }
            } else {
              // 超过1秒，重置计数器
              this.videoRequestCount = 0;
            }
          }
          this.lastVideoRequestTime = now;
          
          this.videoPreloadStatus = 'loading';
          this.isPreloadVideoRequesting = true; // 设置请求锁
          
          // 开始预加载，使用 $nextTick 确保DOM已更新
          this.$nextTick(() => {
            // 再次检查URL和DOM元素
            if (!this.$refs.preloadVideo) {
              if (this.addSystemLog) {
                this.addSystemLog('视频预加载失败：DOM元素不存在');
              }
              this.videoPreloadStatus = 'error';
              this.isPreloadVideoRequesting = false; // 重置请求锁
              return;
            }
            
            if (!this.preloadVideoUrl) {
              if (this.addSystemLog) {
                this.addSystemLog('视频预加载失败：URL为空');
              }
              this.videoPreloadStatus = 'error';
              this.isPreloadVideoRequesting = false; // 重置请求锁
              return;
            }
            
            const videoElement = this.$refs.preloadVideo;
            const currentSrc = videoElement.src || '';
            const targetSrc = this.preloadVideoUrl;
            const readyState = videoElement.readyState;
            
            // 如果src已经设置且相同，检查视频状态
            if (currentSrc === targetSrc && currentSrc) {
              // src相同，检查视频是否已经加载
              if (readyState >= 3) {
                // 视频已经有足够数据，不需要重新加载
                this.videoPreloadStatus = 'loaded';
                this.preloadVideoElement = videoElement;
                this.currentPreloadVideoSrc = targetSrc;
                this.isPreloadVideoRequesting = false; // 重置请求锁
                return;
              }
              // src相同但未加载，需要调用load()
            } else {
              // src不同或为空，需要更新（只在真正变化时才设置）
              if (currentSrc !== targetSrc) {
                // 设置src，但不会自动加载（因为preload="none"）
                videoElement.src = targetSrc;
                this.currentPreloadVideoSrc = targetSrc;
              }
            }
            
            // 只在视频未加载时才调用load()
            // 由于preload="none"，必须手动调用load()才会开始加载
            if (readyState < 3) {
              this.videoLoadCallCount++;
              videoElement.load();
            } else {
              // 视频已加载，直接标记为已加载
              this.videoPreloadStatus = 'loaded';
              this.preloadVideoElement = videoElement;
              this.currentPreloadVideoSrc = targetSrc;
              this.isPreloadVideoRequesting = false; // 重置请求锁
            }
          });
        }, 500); // 延迟500ms
      },

      /**
       * 清理预加载视频（只在组件销毁时调用，其他时候只暂停不清空）
       */
      cleanupPreloadVideo(forceCleanup = false) {
        // forceCleanup为true时才真正清理（用于组件销毁）
        // 平时只暂停视频，保持元素在内存中以便复用
        if (this.videoPreloadTimer) {
          clearTimeout(this.videoPreloadTimer);
          this.videoPreloadTimer = null;
        }
        
        if (this.$refs.preloadVideo) {
          const video = this.$refs.preloadVideo;
          // 先暂停视频
          video.pause();
          
          // 只有在强制清理时才清空src（组件销毁时）
          if (forceCleanup) {
            if (this.cleanupPreloadVideoTimer) {
              clearTimeout(this.cleanupPreloadVideoTimer);
              this.cleanupPreloadVideoTimer = null;
            }
            this.cleanupPreloadVideoTimer = setTimeout(() => {
              this.cleanupPreloadVideoTimer = null;
              if (video && !this.isVideoPlaying) {
                video.src = '';
                video.load();
              }
            }, 300);
            this.preloadVideoElement = null;
            this.videoPreloadStatus = 'idle';
          }
          // 否则只暂停，保持src不变，以便下次复用
        } else if (forceCleanup) {
          // 如果没有ref，但需要强制清理，清空状态
          this.preloadVideoElement = null;
          this.videoPreloadStatus = 'idle';
        }
      },

      /**
       * 处理视频播放状态变化
       */
      handleVideoPlayingChanged(isPlaying) {
        this.isVideoPlaying = isPlaying;
      },

      /**
       * 处理视频播放结束
       */
      handleVideoEnded() {
        // 视频播放完毕，继续轮播到患者页面（第一页）
        if (this.isVideoPage) {
          // 标记视频已停止播放
          this.isVideoPlaying = false;
          // 不清理视频元素，只暂停，保持视频在内存中以便下次复用
          if (this.$refs.preloadVideo) {
            this.$refs.preloadVideo.pause();
            // 重置播放进度到开头，以便下次播放
            this.$refs.preloadVideo.currentTime = 0;
          }
          // 继续轮播，会自动循环到第一页
          if (this.rotateModules) {
            this.rotateModules('next');
          }
        }
      }
    },

    watch: {
      // 监听视频列表变化，自动更新预加载URL
      videoList: {
        handler() {
          this.$nextTick(() => {
            this.updatePreloadVideoUrl();
          });
        },
        immediate: false
      }
    }
  };
}

